import { defineStore } from 'pinia';
import { store } from '@/store';
import { workflowClient } from 'iegsp-api/git.woa.com/itrpcprotocol/iegsp/suppliercgi/workflow/workflow.http';
import { getArea } from '@/utils/area';
import { AreaCode } from '@tencent/iegsp-common/tools/area';

type CountInfo = {
  /** 待接受订单 */
  order_toReceiveOrders_index: number;
  /** 待接受订单-项目类 */
  order_toReceiveOrders_index_project: number;
  /** 待接受订单-素材类 */
  order_toReceiveOrders_index_thing: number;

  /** 待发起交付 */
  delivery_toInitiate_index: number;
  /** 待发起交付-交付被驳回 */
  delivery_toInitiate_index_back: number;
  /** 待发起交付-素材类 */
  delivery_toInitiate_index_thing: number;
  /** 待发起交付-素材类-发起交付 */
  delivery_toInitiate_index_thing_initiate: number;
  /** 待发起交付-素材类-交付被驳回 */
  delivery_toInitiate_index_thing_reject: number;
  /** 待发起交付-项目类 */
  delivery_toInitiate_index_project: number;
  /** 待发起交付-项目类-发起交付 */
  delivery_toInitiate_index_project_initiate: number;
  /** 待发起交付-项目类-交付被驳回 */
  delivery_toInitiate_index_project_reject: number;
  /** 确认验收金额 */
  delivery_confirmAmount_index: number;
  /** 待报价 */
  order_toQuoted_index: number;

  /** 订单变更 */
  order_change_index: number;
  /** 订单变更-素材类 */
  order_change_index_thing: number;
  /** 订单变更-素材类-变更驳回 */
  order_change_index_thing_reject: number;

  /** 报价列表 */
  quote_rfqList_index: number;

  /** 电子签章-WBP 签章权限 */
  electronicSignature_stampPermission_index: number;
  /** 电子签章-WBP 签署合同 */
  electronicSignature_signContract_index: number;

  cn: number;
  na: number;
  eu: number;
  ap: number;
};

const DefaultCount = {
  /** 待接受订单 */
  order_toReceiveOrders_index: 0,
  /** 待接受订单-项目类 */
  order_toReceiveOrders_index_project: 0,
  /** 待接受订单-素材类 */
  order_toReceiveOrders_index_thing: 0,

  /** 待发起交付 */
  delivery_toInitiate_index: 0,
  /** 待发起交付-交付被驳回 */
  delivery_toInitiate_index_back: 0,
  /** 待发起交付-素材类 */
  delivery_toInitiate_index_thing: 0,
  /** 待发起交付-素材类-发起交付 */
  delivery_toInitiate_index_thing_initiate: 0,
  /** 待发起交付-素材类-交付被驳回 */
  delivery_toInitiate_index_thing_reject: 0,
  /** 待发起交付-项目类 */
  delivery_toInitiate_index_project: 0,
  /** 待发起交付-项目类-发起交付 */
  delivery_toInitiate_index_project_initiate: 0,
  /** 待发起交付-项目类-交付被驳回 */
  delivery_toInitiate_index_project_reject: 0,
  /** 确认验收金额 */
  delivery_confirmAmount_index: 0,
  /** 待报价 */
  order_toQuoted_index: 0,

  /** 订单变更 */
  order_change_index: 0,
  /** 订单变更-素材类 */
  order_change_index_thing: 0,
  /** 订单变更-素材类-变更驳回 */
  order_change_index_thing_reject: 0,

  /** 报价列表 */
  quote_rfqList_index: 0,

  /** 电子签章-WBP 签章权限 */
  electronicSignature_stampPermission_index: 0,
  /** 电子签章-WBP 签署合同 */
  electronicSignature_signContract_index: 0,

  /** 艺库供应商能力展示 */
  artcool_index: 0,

  cn: 0,
  na: 0,
  eu: 0,
  ap: 0,
};

export const useCountStore = defineStore('count', {
  state: () => {
    const countInfo: CountInfo = {
      ...DefaultCount,
    };
    return {
      countInfo,
      loaded: false,
      // WBP 待办完整数据（menu_count 数组，key 为路径风格），供头部下拉菜单复用
      wbpMenuCount: {} as Record<string, number>,
      // WBP 待办总数量
      wbpTotal: 0,
    };
  },
  actions: {
    getCount() {
      const isCn = getArea() === AreaCode.cn;
      const countKeys = Object.keys(DefaultCount).filter((key) => {
        // 海外3区不获取 quote_rfqList_index
        if (!isCn && key === 'quote_rfqList_index') return false;
        // WBP 红点由 GetWbpTodoCount 单独获取，无需在 GetTodoCount 中请求
        if (key === 'electronicSignature_stampPermission_index' || key === 'electronicSignature_signContract_index') {
          return false;
        }
        return true;
      });
      // WBP 数量仅国内（cn）获取
      const promiseList: Promise<unknown>[] = [
        workflowClient
          .GetTodoCount({ position: countKeys })
          .then(([res]) => {
            countKeys.forEach((key) => {
              const val = res?.[key] || res?.count?.[key] || 0;
              this.countInfo[key] = val;
            });
          })
          .catch(() => {}),
      ];
      if (isCn) {
        promiseList.push(
          workflowClient
            .GetWbpTodoCount({})
            .then(([res]) => {
              // total 与 count 后端返回均为 string，需转换为 number
              this.wbpTotal = Number(res?.total) || 0;
              // 完整 menu_count 存为 { [key]: number }，供头部下拉组件复用
              const menuCount: Record<string, number> = {};
              (res?.menu_count || []).forEach((item: { key?: string; count?: string | number }) => {
                if (item.key) {
                  menuCount[item.key] = Number(item.count) || 0;
                }
              });
              this.wbpMenuCount = menuCount;
              // WBP 接口的 key 是路径风格（system_contract_stamp / system_contract_sign），
              // 需要映射到 countInfo 的电子签章菜单 key 上，侧边栏才能正确展示
              const wbpKeyMap: Record<string, keyof CountInfo> = {
                system_contract_stamp: 'electronicSignature_stampPermission_index',
                system_contract_sign: 'electronicSignature_signContract_index',
              };
              Object.entries(menuCount).forEach(([key, count]) => {
                const targetKey = wbpKeyMap[key];
                if (targetKey) {
                  this.countInfo[targetKey] = count;
                }
              });
            })
            .catch(() => {}),
        );
      }
      return Promise.all(promiseList).finally(() => {
        this.loaded = true;
      });
    },
  },
  persist: false,
});

export function getCountStore() {
  return useCountStore(store);
}
